import { G } from '@mobily/ts-belt';
import { z } from 'zod';

import { ZodAmount } from '@Common/types/Amount';
import { ZodB2bSavingPlanCategory } from '@Common/types/b2b/plans';
import { ZodDate } from '@Common/types/date';
import { ZodB2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';
import { ZodSocialSecurityContributions } from '@Common/types/funding/withdrawal_social_contributions';
import {
  ZodInstrumentMutualFundAssetClass,
  ZodMutualFundValuationFrequency,
} from '@Common/types/instruments/instrument';
import { ZodTransformedProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { ZodDateLike } from '@Common/types/zod/zod-date-like';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodMaxBase64SizeValidator } from '@Common/types/zod/zod-max-base64-size';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodB2BWithdrawalDocument = z.object({
  document_file: createZodMaxBase64SizeValidator('7mb'),
  document_name: ZodTrimmedString,
  date_of_expiry: ZodDate.optional(),
});

const ZodFundingWithdrawEarlyRequestParams = createZodParamsValidator(
  z.object({
    withdrawalId: ZodTrimmedString,
  }),
);

const ZodGetFundingWithdrawEarlyRequestDocument = z.object({
  name: ZodTrimmedString,
  type: z.enum(['early_withdrawal_request', 'withdrawal_national_identity']),
  document_id: ZodId,
});

export const getFundingWithdrawRequestDocumentsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodFundingWithdrawEarlyRequestParams,
  responseBody: z.object({
    documents: ZodGetFundingWithdrawEarlyRequestDocument.array(),
  }),
});

export type GetFundingWithdrawRequestDocumentsEndpoint = GetEndpointType<
  typeof getFundingWithdrawRequestDocumentsEndpoint
>;

export const putFundingWithdrawEarlyRequestDocumentsEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodFundingWithdrawEarlyRequestParams,
  body: z.object({
    documents: ZodB2BWithdrawalDocument.omit({ date_of_expiry: true }).array().min(1).max(5),
  }),
  responseBody: ZodSuccessResponse,
});

export type PutFundingWithdrawEarlyRequestDocumentsEndpoint = GetEndpointType<
  typeof putFundingWithdrawEarlyRequestDocumentsEndpoint
>;

export const ZodWithdrawalSourceType = z.enum(['locked', 'withdrawable']);

export const ZodWithdrawalAllocation = z.object({
  saving_plan_id: ZodTransformedProfileSavingPlanId,
  amount: z.number().int().min(1),
  instrument_id: z.number().int().optional(),
  type: ZodWithdrawalSourceType,
});

export type B2BWithdrawalAllocation = z.infer<typeof ZodWithdrawalAllocation>;

export const ZodWithdrawalAllocations = z
  .array(ZodWithdrawalAllocation)
  .min(1)
  .refine(
    (allocations) => {
      const uniqueAllocationKeys = new Set(
        allocations.map((allocation) => {
          const savingPlanIdKey = G.isString(allocation.saving_plan_id)
            ? allocation.saving_plan_id
            : `${allocation.saving_plan_id.investment_account_id}-${allocation.saving_plan_id.saving_plan_id}`;
          return `${savingPlanIdKey}-${G.isNotNullable(allocation.instrument_id) ? allocation.instrument_id : 'amount'}-${allocation.type}`;
        }),
      );

      return uniqueAllocationKeys.size === allocations.length;
    },
    {
      message: 'Allocations must be unique',
      path: ['allocations'],
    },
  );

export const ZodEarlyWithdrawalRequest = z.object({
  reason: ZodB2BEarlyWithdrawalReason,
  date: ZodDateLike.optional(),
});

export type B2BEarlyWithdrawalRequest = z.infer<typeof ZodEarlyWithdrawalRequest>;

export function withEarlyWithdrawalRequestRefine<
  Schema extends z.ZodType<{ allocations: { type: string }[]; request?: unknown }>,
>(schema: Schema) {
  return schema.refine(
    ({ allocations, request }) =>
      allocations.every((allocation) => allocation.type === 'withdrawable') ||
      G.isNotNullable(request),
    {
      message: 'Allocations must be withdrawable or request must be provided',
      path: ['allocations'],
    },
  );
}

const ZodPostFundingWithdrawEmployeeSavingsSummaryRequestBodyObject = z.object({
  company_id: z.number().int(),
  allocations: ZodWithdrawalAllocations,
  request: ZodEarlyWithdrawalRequest.optional(),
});

const ZodPostFundingWithdrawEmployeeSavingsSummaryRequestBody = withEarlyWithdrawalRequestRefine(
  ZodPostFundingWithdrawEmployeeSavingsSummaryRequestBodyObject,
);

export type B2BWithdrawalRequestSummaryBody = z.infer<
  typeof ZodPostFundingWithdrawEmployeeSavingsSummaryRequestBody
>;

export const ZodWithdrawalSummaryInstrumentAllocation = z.object({
  amount: ZodAmount,
  total_amount: ZodAmount,
  instrument_id: ZodId,
  instrument_name: ZodTrimmedString,
  instrument_risk: z.number().int().min(0).max(7).nullable(),
  instrument_asset_class: ZodInstrumentMutualFundAssetClass.or(z.enum(['ccb', 'euro-fund'])),
  instrument_pricing_frequency: ZodMutualFundValuationFrequency,
});

export type B2BWithdrawalSummaryInstrumentAllocation = z.infer<
  typeof ZodWithdrawalSummaryInstrumentAllocation
>;

export const ZodWithdrawalSummaryAmounts = z.object({
  gross_amount: ZodAmount,
  net_amount: ZodAmount,
  tax_amount: ZodAmount.nullable(),
  allocations: z.array(ZodWithdrawalSummaryInstrumentAllocation),
});

export type B2BWithdrawalSummaryAmounts = z.infer<typeof ZodWithdrawalSummaryAmounts>;

const ZodTaxInfo = z.object({
  rate: z.number(),
  amount: ZodAmount,
});

export const ZodWithdrawalTaxBreakdown = z.object({
  voluntary_contributions: z.number().optional(),
  employee: z.object({
    capital_gain: z.number(),
    social_security_contributions: ZodSocialSecurityContributions,
    pfonl: ZodTaxInfo.nullable(),
    withholding_tax: ZodTaxInfo.nullable(),
  }),
  employer: z
    .object({
      capital_gain: z.number(),
      social_security_contributions: ZodSocialSecurityContributions,
      pfonl: ZodTaxInfo.nullable(),
    })
    .nullable(),
});

export type B2BWithdrawalTaxBreakdown = z.infer<typeof ZodWithdrawalTaxBreakdown>;

export const ZodWithdrawalSummary = z.object({
  type: ZodWithdrawalSourceType,
  gross_amount: ZodAmount,
  net_amount: ZodAmount,
  tax_amount: ZodAmount.nullable(),
  allocations: z.array(ZodWithdrawalSummaryInstrumentAllocation),
  products: z.array(ZodB2bSavingPlanCategory),
  products_breakdown: z.partialRecord(
    ZodB2bSavingPlanCategory,
    z.object({
      ...ZodWithdrawalSummaryAmounts.shape,
      requires_approval: z.boolean(),
    }),
  ),
  tax_breakdown: z.partialRecord(ZodB2bSavingPlanCategory, ZodWithdrawalTaxBreakdown.nullable()),
});

export type B2BWithdrawalSummary = z.infer<typeof ZodWithdrawalSummary>;

const ZodPostFundingWithdrawEmployeeSavingsSummaryResponseBody = z.object({
  summaries: z.array(ZodWithdrawalSummary),
  per_valuation_frequency: z
    .partialRecord(ZodMutualFundValuationFrequency, z.array(ZodWithdrawalSummary))
    .optional(),
});

export type PostFundingWithdrawEmployeeSavingsSummaryResponseBody = z.infer<
  typeof ZodPostFundingWithdrawEmployeeSavingsSummaryResponseBody
>;

export const postFundingWithdrawEmployeeSavingsSummaryEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodPostFundingWithdrawEmployeeSavingsSummaryRequestBody,
  responseBody: ZodPostFundingWithdrawEmployeeSavingsSummaryResponseBody,
  headers: undefined,
});

export type PostFundingWithdrawEmployeeSavingsSummaryEndpoint = GetEndpointType<
  typeof postFundingWithdrawEmployeeSavingsSummaryEndpoint
>;

// Zod forbids overwriting keys of refined objects via `.extend()`, so derive from this one
export const ZodPostFundingWithdrawEmployeeSavingsRequestBodyObject = z.object({
  ...ZodPostFundingWithdrawEmployeeSavingsSummaryRequestBodyObject.shape,
  split_per_valuation_frequency: z.boolean().optional(),
  mdlr_beneficiary_id: z.number().int().nullable(),
  documents: z
    .array(
      z.object({
        ...ZodB2BWithdrawalDocument.shape,
        type: z.literal('early_withdrawal_request'),
      }),
    )
    .max(5)
    .min(1)
    .optional(),
});

const ZodPostFundingWithdrawEmployeeSavingsRequestBody = withEarlyWithdrawalRequestRefine(
  ZodPostFundingWithdrawEmployeeSavingsRequestBodyObject,
);

export type PostFundingWithdrawEmployeeSavingsRequestBody = z.infer<
  typeof ZodPostFundingWithdrawEmployeeSavingsRequestBody
>;

export const postFundingWithdrawEmployeeSavingsEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodPostFundingWithdrawEmployeeSavingsRequestBody,
  responseBody: z.object({
    withdrawals: z.array(
      z.object({
        id: z.number().int(),
        product: ZodB2bSavingPlanCategory,
      }),
    ),
  }),
  headers: undefined,
});

export type PostFundingWithdrawEmployeeSavingsEndpoint = GetEndpointType<
  typeof postFundingWithdrawEmployeeSavingsEndpoint
>;
